import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Badge } from "@/components/ui/badge";
import { MagazinTabs } from "./Veranstaltungen";

import { Button } from "@/components/ui/button";
import { BLOG_CATEGORIES, categoryLabel, localizePost, formatPostDate } from "@/lib/blog";
import { useLang, useT, useLocalePath } from "@/i18n";
import { Calendar, Search, X } from "lucide-react";
import { useSeo, absoluteUrl } from "@/lib/seo";

interface Post {
  id: string;
  title: string;
  slug: string;
  excerpt: string | null;
  cover_image_url: string | null;
  category: string;
  published_at: string | null;
  title_en: string | null;
  slug_en: string | null;
  excerpt_en: string | null;
}

const Blog = () => {
  const lang = useLang();
  const lp = useLocalePath();
  const t = useT(
    {
      seoTitle: "Magazin: Mikrobiom & Darmgesundheit | PRAXIS IB Wien",
      seoDescription:
        "Fachartikel zu Mikrobiom, Darmgesundheit, SIBO, Immunsystem und Naturheilkunde – von Isabella Bartak, PRAXIS IB in Wien.",
      eyebrow: "Magazin",
      heading: "Wissen für Mikrobiom, Darm & Immunsystem",
      intro:
        "Fundierte Fachartikel rund um ganzheitliche Gesundheit, Mikrobiom, Darmgesundheit, Mikronährstoffe und Naturheilkunde. Lesen Sie nach Interesse oder Thema.",
      all: "Alle",
      loading: "Lade Beiträge…",
      empty: "Aktuell keine Beiträge vorhanden.",
      searchPlaceholder: "Artikel suchen …",
      searchLabel: "Magazin durchsuchen",
      noResults: "Keine passenden Artikel gefunden.",
      resetSearch: "Suche zurücksetzen",
    },
    {
      seoTitle: "Magazine: microbiome & gut health | PRAXIS IB Vienna",
      seoDescription:
        "In-depth articles on the microbiome, gut health, SIBO, immunity and naturopathy – by Isabella Bartak, PRAXIS IB in Vienna.",
      eyebrow: "Magazine",
      heading: "Knowledge on the microbiome, gut & immune system",
      intro:
        "Well-founded articles on holistic health, the microbiome, gut health, micronutrients and naturopathy. Browse by interest or topic.",
      all: "All",
      loading: "Loading articles…",
      empty: "No articles available at the moment.",
      searchPlaceholder: "Search articles …",
      searchLabel: "Search the magazine",
      noResults: "No matching articles found.",
      resetSearch: "Clear search",
    },
  );
  const [posts, setPosts] = useState<Post[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<string | null>(null);
  const [search, setSearch] = useState("");

  const jsonLd = useMemo(
    () => [
      {
        "@context": "https://schema.org",
        "@type": "Blog",
        name: "Magazin – PRAXIS IB",
        description:
          "Magazin der Praxis Innere Balance: Beiträge zu Mikrobiom, Ernährung, Geist & Seele und Naturheilkunde.",
        url: "https://ibpraxis.net/blog",
        inLanguage: lang === "en" ? "en" : "de-AT",
        publisher: { "@type": "Organization", name: "PRAXIS IB", url: "https://ibpraxis.net" },
        blogPost: posts.slice(0, 20).map((p) => ({
          "@type": "BlogPosting",
          headline: p.title,
          url: absoluteUrl(`/blog/${p.slug}`),
          datePublished: p.published_at,
          image: p.cover_image_url ?? undefined,
          articleSection: categoryLabel(p.category, lang),
        })),
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Startseite", item: "https://ibpraxis.net/" },
          { "@type": "ListItem", position: 2, name: "Magazin", item: "https://ibpraxis.net/blog" },
        ],
      },
    ],
    [posts, lang]
  );

  useSeo({
    title: t.seoTitle,
    description: t.seoDescription,
    canonical: "/blog",
    jsonLd,
    jsonLdId: "blog-list-jsonld",
  });

  useEffect(() => {
    (async () => {
      setLoading(true);
      let query = supabase
        .from("blog_posts")
        .select("id,title,slug,excerpt,cover_image_url,category,published_at,title_en,slug_en,excerpt_en")
        .eq("published", true)
        .order("published_at", { ascending: false });
      if (filter) query = query.eq("category", filter as any);
      const { data } = await query;
      setPosts(data ?? []);
      setLoading(false);
    })();
  }, [filter]);

  const localized = posts.map((p) => ({ ...p, ...localizePost(p, lang) }));

  const normalize = (s: string) =>
    s
      .toLowerCase()
      .replace(/ä/g, "ae")
      .replace(/ö/g, "oe")
      .replace(/ü/g, "ue")
      .replace(/ß/g, "ss");

  const query = normalize(search.trim());
  const visible = useMemo(
    () =>
      query
        ? localized.filter((p) =>
            normalize(
              `${p.title} ${p.excerpt ?? ""} ${categoryLabel(p.category, lang)}`
            ).includes(query)
          )
        : localized,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [posts, lang, query]
  );
  const isSearching = query.length > 0;
  const featured = isSearching ? undefined : visible[0];
  const rest = isSearching ? visible : visible.slice(1);

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="pt-24 pb-20">
        <div className="container mx-auto px-4">
          {/* Header */}
          <div className="text-center max-w-3xl mx-auto mb-8">
            <p className="text-sm font-medium text-primary uppercase tracking-wider mb-2">{t.eyebrow}</p>
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-foreground mb-4">
              {t.heading}
            </h1>
            <p className="text-muted-foreground text-base md:text-lg leading-relaxed mb-8">{t.intro}</p>
          </div>

          <MagazinTabs active="blog" />

          {/* Search */}
          <div className="max-w-xl mx-auto mb-8">
            <div className="relative">
              <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={t.searchPlaceholder}
                aria-label={t.searchLabel}
                className="w-full rounded-full border border-border bg-card pl-10 pr-10 py-2.5 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary transition-colors"
              />
              {search && (
                <button
                  type="button"
                  onClick={() => setSearch("")}
                  aria-label={t.resetSearch}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-primary transition-colors"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>

          {/* Filters */}
          <div className="flex flex-wrap justify-center gap-2 mb-12">
            <Button
              variant={filter === null ? "default" : "outline"}
              size="sm"
              onClick={() => setFilter(null)}
            >
              {t.all}
            </Button>
            {BLOG_CATEGORIES.map((c) => (
              <Button
                key={c.value}
                variant={filter === c.value ? "default" : "outline"}
                size="sm"
                onClick={() => setFilter(c.value)}
              >
                {lang === "en" ? c.labelEn : c.label}
              </Button>
            ))}
          </div>

          {loading ? (
            <p className="text-center text-muted-foreground">{t.loading}</p>
          ) : posts.length === 0 ? (
            <div className="text-center py-16">
              <p className="text-muted-foreground">{t.empty}</p>
            </div>
          ) : visible.length === 0 ? (
            <div className="text-center py-16">
              <p className="text-muted-foreground mb-4">{t.noResults}</p>
              <Button variant="outline" size="sm" onClick={() => setSearch("")}>
                {t.resetSearch}
              </Button>
            </div>
          ) : (
            <>
              {/* Featured */}
              {featured && (
                <Link
                  to={lp(`/blog/${featured.slug}`)}
                  className="block group mb-16 rounded-2xl overflow-hidden border border-border bg-card hover:shadow-xl transition-shadow"
                >
                  <div className="grid md:grid-cols-2 gap-0">
                    {featured.cover_image_url && (
                      <div className="aspect-[4/3] md:aspect-auto overflow-hidden bg-muted">
                        <img
                          src={featured.cover_image_url}
                          alt={featured.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        />
                      </div>
                    )}
                    <div className="p-8 md:p-10 flex flex-col justify-center">
                      <Badge variant="secondary" className="w-fit mb-3">
                        {categoryLabel(featured.category, lang)}
                      </Badge>
                      <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-3 group-hover:text-primary transition-colors">
                        {featured.title}
                      </h2>
                      {featured.excerpt && (
                        <p className="text-muted-foreground mb-4 line-clamp-3">{featured.excerpt}</p>
                      )}
                      <div className="flex items-center gap-2 text-xs text-muted-foreground">
                        <Calendar className="w-3.5 h-3.5" />
                        {formatPostDate(featured.published_at, lang)}
                      </div>
                    </div>
                  </div>
                </Link>
              )}

              {/* Grid */}
              {rest.length > 0 && (
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                  {rest.map((post) => (
                    <Link
                      key={post.id}
                      to={lp(`/blog/${post.slug}`)}
                      className="group rounded-xl overflow-hidden border border-border bg-card hover:shadow-lg transition-shadow flex flex-col"
                    >
                      {post.cover_image_url && (
                        <div className="aspect-[16/10] overflow-hidden bg-muted">
                          <img
                            src={post.cover_image_url}
                            alt={post.title}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                            loading="lazy"
                          />
                        </div>
                      )}
                      <div className="p-5 flex-1 flex flex-col">
                        <Badge variant="secondary" className="w-fit mb-2 text-xs">
                          {categoryLabel(post.category, lang)}
                        </Badge>
                        <h3 className="font-semibold text-lg text-foreground mb-2 group-hover:text-primary transition-colors line-clamp-2">
                          {post.title}
                        </h3>
                        {post.excerpt && (
                          <p className="text-sm text-muted-foreground mb-3 line-clamp-3 flex-1">{post.excerpt}</p>
                        )}
                        <div className="flex items-center gap-2 text-xs text-muted-foreground mt-auto">
                          <Calendar className="w-3 h-3" />
                          {formatPostDate(post.published_at, lang)}
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </>
          )}
        </div>

      </main>
      <Footer />
    </div>
  );
};

export default Blog;
